import { useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ro } from "date-fns/locale";
import { Controller, useForm } from "react-hook-form";
import GenericModal from "../GenericModal";
import { Vocabulary } from "../../Utils/Vocabulary";
import { endpoints } from "../../Utils/UrlEnum";
import { FormsIds } from "../../Utils/Constants";
import { EOnlineLessonTargetScope, OnlineLessonScopeOptions } from "../../Utils/Enums";
import { OnlineLessonModel } from "../../Utils/Models";
import { getData } from "../../Services/getData";
import { postData } from "../../Services/postData";
import { updateData } from "../../Services/updateData";
import genericStyles from "../../Styles/genericStyles.module.css";

interface OnlineLessonFormProps {
  lesson: OnlineLessonModel | null;
  open: boolean;
  onClose: () => void;
  onSaved: (count?: number) => void;
}


export default function OnlineLessonForm({
  lesson,
  open,
  onClose,
  onSaved,
}: OnlineLessonFormProps) {
  const isEdit = !!(lesson && lesson._id);
  const [instructors, setInstructors] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [selectedInstructor, setSelectedInstructor] = useState<any>(null);
  const [selectedProducts, setSelectedProducts] = useState<any[]>([]);

  const { register, handleSubmit, control, reset, watch, setValue } = useForm({
    defaultValues: {
      title: "",
      startDate: null as Date | null,
      targetScope: EOnlineLessonTargetScope.SPECIFIC,
      isRepeat: false,
      repeatIntervalDays: 7,
      numberOfRepetitions: 3,
    },
  });

  const targetScope = watch("targetScope");
  const isRepeat = watch("isRepeat");

  const getRealInstructorId = (instructor: any) => {
    if (!instructor) return null;
    if (typeof instructor === "string") return instructor;
    return instructor?.instructorDetails?.[0]?._id || instructor?._id || null;
  };

  useEffect(() => {
    getData(`${endpoints.instructor.user}/paginated?page=0&perPage=100000`).then(
      (res: any) => {
        if (res?.data?.results) setInstructors(res.data.results);
      }
    );
    getData(`${endpoints.product.main}/paginated?page=0&perPage=100000`).then(
      (res: any) => {
        if (res?.data?.results) setProducts(res.data.results);
        else if (Array.isArray(res?.data)) setProducts(res.data);
      }
    );
  }, []);

  useEffect(() => {
    if (lesson) {
      const l = lesson as any;
      reset({
        title: l.title || "",
        startDate: l.startDate ? new Date(l.startDate) : null,
        targetScope: l.targetScope || EOnlineLessonTargetScope.SPECIFIC,
        isRepeat: l.isRepeat ?? false,
        repeatIntervalDays: l.repeatIntervalDays ?? 7,
        numberOfRepetitions: l.numberOfRepetitions ?? 3,
      });

      const instructorId = getRealInstructorId(l.instructorId);
      const found = instructors.find(
        (i: any) => getRealInstructorId(i) === instructorId
      );
      setSelectedInstructor(found || null);

      const ids: string[] = (l.productIds || []).map((p: any) =>
        typeof p === "object" ? p._id : p
      );
      setSelectedProducts(products.filter((p: any) => ids.includes(p._id)));
    } else {
      reset({
        title: "",
        startDate: null,
        targetScope: EOnlineLessonTargetScope.SPECIFIC,
        isRepeat: false,
        repeatIntervalDays: 7,
        numberOfRepetitions: 3,
      });
      setSelectedInstructor(null);
      setSelectedProducts([]);
    }
  }, [lesson, instructors, products, reset]);

  const onSubmit = async (data: any) => {
    const dto: any = {
      title: data.title,
      startDate: data.startDate,
      instructorId: getRealInstructorId(selectedInstructor),
      targetScope: data.targetScope,
      isRepeat: data.isRepeat,
    };

    if (data.targetScope === EOnlineLessonTargetScope.SPECIFIC) {
      dto.productIds = selectedProducts.map((p: any) => p._id);
    }

    if (data.isRepeat) {
      dto.repeatIntervalDays = data.repeatIntervalDays;
      dto.numberOfRepetitions = data.numberOfRepetitions;
    }

    try {
      if (isEdit && lesson?._id) {
        const res = await updateData(`${endpoints.onlineLessons.main}/${lesson._id}`, dto);
        if (res?.data) {
          onSaved(1);
          onClose();
        }
      } else {
        const res = await postData(endpoints.onlineLessons.main, dto);
        if (res?.data) {
          const count = Array.isArray(res.data) ? res.data.length : 1;
          onSaved(count);
          onClose();
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <GenericModal
      open={open}
      onClose={onClose}
      title={isEdit ? Vocabulary.edit : Vocabulary.addOnlineLesson}
      actions={
        <div className={genericStyles.modalActions}>
          <Button color="secondary" onClick={onClose} variant="outlined" fullWidth>
            {Vocabulary.cancel}
          </Button>
          <Button fullWidth type="submit" form={FormsIds.onlineLessonForm} variant="contained">
            {Vocabulary.save}
          </Button>
        </div>
      }
    >
      <form id={FormsIds.onlineLessonForm} onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2} sx={{ pt: 1 }}>

          {/* Title */}
          <Grid item xs={12}>
            <TextField
              {...register("title", { required: true })}
              label={Vocabulary.onlineLessonTitle}
              fullWidth
              required
              disabled={!selectedInstructor}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Date & Time */}
          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ro}>
              <Controller
                name="startDate"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <DateTimePicker
                    label={Vocabulary.onlineLessonScheduledAt}
                    value={field.value}
                    onChange={field.onChange}
                    slotProps={{
                      textField: { fullWidth: true, required: true, InputLabelProps: { shrink: true } },
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>

          {/* Instructor */}
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={instructors}
              getOptionLabel={(option: any) =>
                `${option.lastName || ""} ${option.firstName || ""}`.trim() || option.name || ""
              }
              value={selectedInstructor}
              onChange={(_, value) => {
                setSelectedInstructor(value);
                if (value) {
                  const name = `${value.lastName || ""} ${value.firstName || ""}`.trim() || value.name || "";
                  setValue("title", name);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={Vocabulary.onlineLessonInstructor}
                  required
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>

          {/* Target Scope */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel shrink>{Vocabulary.onlineLessonTargetScope}</InputLabel>
              <Controller
                name="targetScope"
                control={control}
                render={({ field }) => (
                  <Select {...field} label={Vocabulary.onlineLessonTargetScope} notched>
                    {OnlineLessonScopeOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.name}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </Grid>

          {/* Products — only when scope = specific */}
          {targetScope === EOnlineLessonTargetScope.SPECIFIC && (
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={products}
                getOptionLabel={(option: any) => option.name || ""}
                value={selectedProducts}
                onChange={(_, value) => setSelectedProducts(value)}
                renderInput={(params) => (
                  <TextField {...params} label={Vocabulary.products} InputLabelProps={{ shrink: true }} />
                )}
              />
            </Grid>
          )}

          {/* Repeat toggle */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Controller
                  name="isRepeat"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  )}
                />
              }
              label={Vocabulary.onlineLessonRepeat}
            />
          </Grid>

          {/* Repeat fields — only when isRepeat = true */}
          {isRepeat && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  {...register("repeatIntervalDays", { required: true, valueAsNumber: true })}
                  label={Vocabulary.onlineLessonRepeatInterval}
                  type="number"
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  {...register("numberOfRepetitions", { required: true, valueAsNumber: true })}
                  label={Vocabulary.onlineLessonRepetitions}
                  type="number"
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  {`${Vocabulary.onlineLessonRepetitions}: ${watch("numberOfRepetitions")} ×  ${watch("repeatIntervalDays")} ${Vocabulary.days || "zile"} → ${watch("numberOfRepetitions") + 1} ${Vocabulary.onlineLessonCreatedCount}`}
                </Typography>
              </Grid>
            </>
          )}

        </Grid>
      </form>
    </GenericModal>
  );
}
